// 2026-10-04 — full rewrite of the browse UI, per repeated, explicit
// request: the old left sidebar was a narrow (288px) recursive indented
// tree — "so many folders just vertically... it becomes impossible."
// Replaced with a real file-explorer: breadcrumb navigation + a wrapping
// GRID of folder/file tiles (Finder/Explorer-style — click a folder tile
// to go into it, click a file tile to preview it), one level at a time
// instead of a fully-expanded nested tree. Preview/Download/Delete logic
// (PreviewPanel) is untouched — this only replaces how folders/files are
// browsed and found, not what happens once a file is selected.
//
// Also adds real structured filters (First/Last Name, DOB, Report Kind,
// Report Type, Accession) alongside the existing free-text filename
// search — backed by /api/reports/search's own first_name/last_name/dob/
// accession/report_type_id/report_kind params (already built server-side).
// Accession only applies to Patient/Clinician AI reports (requisitions
// have no accession number) — disabled/hidden unless Kind is one of those
// two, per explicit instruction.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../api";
import { extractErrorDetail } from "./validators";
import {
  Box, Typography, IconButton, CircularProgress, Tooltip,
  Chip, Button, TextField, InputAdornment, Select, MenuItem, Collapse,
  Checkbox, Alert,
} from "@mui/material";
import {
  Folder, PictureAsPdf, Download, Delete, Refresh, CloudDone,
  PersonOutline, LocalHospital, Science, NavigateNext, Search, Close,
  FilterList, Home, CheckBoxOutlineBlank, DeleteSweep,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import PageShell from "./PageShell";
import ConfirmDialog from "./ConfirmDialog";
import { formatEST } from "../dateUtils";

const B    = { navy: "#00357a", navyDark: "#001f4d", navyMid: "#004299", gold: "#c9a96e" };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(2)} MB`;
}

function formatDate(iso) {
  return formatEST(iso); // 2026-10-04 — explicit instruction: every timestamp in this app shows in EST, labeled
}

function folderDisplayName(name) {
  if (name === "patient_reports")   return "Patient Reports";
  if (name === "clinician_reports") return "Clinician Reports";
  if (name === "converted_reports") return "Converted Requisitions";
  return name;
}

function folderIcon(name) {
  if (name === "patient_reports")   return <PersonOutline sx={{ fontSize: 28, color: "#60a5fa" }} />;
  if (name === "clinician_reports") return <LocalHospital sx={{ fontSize: 28, color: "#34d399" }} />;
  if (name === "converted_reports") return <Science sx={{ fontSize: 28, color: B.gold }} />;
  return <Folder sx={{ fontSize: 28, color: B.gold }} />;
}

// ─── FolderTile / FileTile — the grid explorer's building blocks ──────────────

// 2026-10-05 — explicit request: multi-select + bulk delete, and folder
// delete (previously only single-file delete existed anywhere in this
// page). selectMode shows a checkbox on every tile instead of navigating/
// previewing on click; checked tracks which prefix/blob_names are picked.
function FolderTile({ name, prefix, onClick, selectMode, checked, onToggleCheck }) {
  const handleClick = (e) => {
    if (selectMode) { e.stopPropagation(); onToggleCheck(prefix); return; }
    onClick();
  };
  return (
    <Box onClick={handleClick} sx={{
      position: "relative",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75,
      width: 120, p: 1.5, borderRadius: "12px", cursor: "pointer",
      bgcolor: checked ? "rgba(192,168,140,0.14)" : "transparent",
      "&:hover": { bgcolor: checked ? "rgba(192,168,140,0.18)" : "rgba(255,255,255,0.06)" },
      transition: "background 0.15s",
    }}>
      {selectMode && (
        <Checkbox size="small" checked={checked} onChange={() => onToggleCheck(prefix)}
          icon={<CheckBoxOutlineBlank sx={{ fontSize: 18, color: "rgba(255,255,255,0.3)" }} />}
          sx={{ position: "absolute", top: 2, left: 2, p: 0.5, color: B.gold, "&.Mui-checked": { color: B.gold } }} />
      )}
      <Box sx={{ width: 56, height: 56, borderRadius: "14px", bgcolor: "rgba(255,255,255,0.05)",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        {folderIcon(name)}
      </Box>
      <Typography sx={{
        fontSize: "0.74rem", color: "rgba(255,255,255,0.85)", textAlign: "center",
        wordBreak: "break-word", lineHeight: 1.3, maxWidth: "100%",
      }}>
        {folderDisplayName(name)}
      </Typography>
    </Box>
  );
}

function FileTile({ file, selected, onClick, selectMode, checked, onToggleCheck }) {
  const handleClick = (e) => {
    if (selectMode) { e.stopPropagation(); onToggleCheck(file.blob_name); return; }
    onClick();
  };
  return (
    <Box onClick={handleClick} sx={{
      position: "relative",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75,
      width: 120, p: 1.5, borderRadius: "12px", cursor: "pointer",
      bgcolor: (selected || checked) ? "rgba(192,168,140,0.16)" : "transparent",
      border: (selected || checked) ? `1px solid ${B.gold}` : "1px solid transparent",
      "&:hover": { bgcolor: (selected || checked) ? "rgba(192,168,140,0.2)" : "rgba(255,255,255,0.06)" },
      transition: "all 0.15s",
    }}>
      {selectMode && (
        <Checkbox size="small" checked={checked} onChange={() => onToggleCheck(file.blob_name)}
          icon={<CheckBoxOutlineBlank sx={{ fontSize: 18, color: "rgba(255,255,255,0.3)" }} />}
          sx={{ position: "absolute", top: 2, left: 2, p: 0.5, color: B.gold, "&.Mui-checked": { color: B.gold } }} />
      )}
      <Box sx={{ width: 56, height: 56, borderRadius: "14px",
        bgcolor: selected ? "rgba(192,168,140,0.18)" : "rgba(255,255,255,0.05)",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <PictureAsPdf sx={{ fontSize: 26, color: selected ? B.gold : "#94a3b8" }} />
      </Box>
      <Typography sx={{
        fontSize: "0.72rem", color: selected ? "#f8e9d6" : "rgba(255,255,255,0.8)",
        fontWeight: selected ? 600 : 400, textAlign: "center",
        wordBreak: "break-word", lineHeight: 1.3, maxWidth: "100%",
      }}>
        {file.filename}
      </Typography>
      <Typography sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.3)" }}>
        {formatBytes(file.size_bytes)}
      </Typography>
    </Box>
  );
}

// ─── Breadcrumbs — used both for the explorer path and the preview panel ──────

function Breadcrumbs({ parts, onNavigate, dark }) {
  const color1 = dark ? "rgba(255,255,255,0.45)" : "#64748b";
  const color2 = dark ? "#fff" : B.navy;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
      <Box onClick={() => onNavigate(-1)} sx={{ display: "flex", alignItems: "center", cursor: "pointer", color: color1, "&:hover": { color: color2 } }}>
        <Home sx={{ fontSize: 16 }} />
      </Box>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          <NavigateNext sx={{ fontSize: 14, color: color1 }} />
          <Typography
            onClick={() => onNavigate(i)}
            sx={{
              fontSize: 13.5, cursor: "pointer",
              color: i === parts.length - 1 ? color2 : color1,
              fontWeight: i === parts.length - 1 ? 700 : 400,
              "&:hover": { color: color2 },
            }}>
            {folderDisplayName(part)}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

// ─── Explorer — one-level-at-a-time grid browser ───────────────────────────────

function Explorer({ currentPrefix, onNavigateInto, onNavigateToCrumb, onFileSelect, selectedBlob, reloadKey, selectMode, checkedItems, onToggleCheck }) {
  const [entries, setEntries] = useState(null); // {folders, files} | null
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await api.get("/api/reports/browse", { params: { prefix: currentPrefix } });
      setEntries(res.data);
    } catch (e) {
      setError(extractErrorDetail(e, "Failed to load."));
    }
    setLoading(false);
  }, [currentPrefix]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const parts = currentPrefix.replace(/\/$/, "").split("/").filter(Boolean);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Breadcrumbs parts={parts} onNavigate={onNavigateToCrumb} dark />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress size={20} sx={{ color: B.gold }} />
          </Box>
        ) : error ? (
          <Typography sx={{ fontSize: "0.78rem", color: "#f87171", textAlign: "center", pt: 3 }}>{error}</Typography>
        ) : entries && entries.folders.length === 0 && entries.files.length === 0 ? (
          <Box sx={{ textAlign: "center", pt: 4 }}>
            <CloudDone sx={{ fontSize: 32, color: "rgba(255,255,255,0.1)", mb: 1 }} />
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.2)" }}>
              This folder is empty.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {(entries?.folders || []).map((f) => {
              const name = f.replace(/\/$/, "").split("/").pop();
              return (
                <FolderTile key={f} name={name} prefix={f} onClick={() => onNavigateInto(f)}
                  selectMode={selectMode} checked={checkedItems?.has(f)} onToggleCheck={onToggleCheck} />
              );
            })}
            {(entries?.files || []).map((file) => (
              <FileTile key={file.blob_name} file={file}
                selected={selectedBlob === file.blob_name}
                onClick={() => onFileSelect(file)}
                selectMode={selectMode} checked={checkedItems?.has(file.blob_name)} onToggleCheck={onToggleCheck} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Search results — same tile-grid treatment as the explorer ────────────────

function SearchResultsGrid({ results, loading, error, query, onFileSelect, selectedBlob }) {
  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}><CircularProgress size={20} sx={{ color: B.gold }} /></Box>;
  }
  if (error) {
    return <Typography sx={{ fontSize: "0.78rem", color: "#f87171", textAlign: "center", pt: 3 }}>{error}</Typography>;
  }
  if (results.length === 0) {
    return (
      <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.3)", textAlign: "center", pt: 4 }}>
        No matches{query ? ` for "${query}"` : ""} with these filters.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 2 }}>
      {results.filter((r) => r.gcs_lab_internal_path).map((r) => {
        const blob = r.gcs_lab_internal_path;
        const filename = blob.split("/").pop();
        const selected = selectedBlob === blob;
        return (
          <Box key={r._id}
            onClick={() => onFileSelect({ blob_name: blob, filename })}
            sx={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75,
              width: 130, p: 1.5, borderRadius: "12px", cursor: "pointer",
              bgcolor: selected ? "rgba(192,168,140,0.16)" : "transparent",
              border: selected ? `1px solid ${B.gold}` : "1px solid transparent",
              "&:hover": { bgcolor: selected ? "rgba(192,168,140,0.2)" : "rgba(255,255,255,0.06)" },
              transition: "all 0.15s",
            }}>
            <Box sx={{ width: 56, height: 56, borderRadius: "14px",
              bgcolor: selected ? "rgba(192,168,140,0.18)" : "rgba(255,255,255,0.05)",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              {r.report_kind === "patient" ? <PersonOutline sx={{ fontSize: 24, color: "#60a5fa" }} />
                : r.report_kind === "clinician" ? <LocalHospital sx={{ fontSize: 24, color: "#34d399" }} />
                : <Science sx={{ fontSize: 24, color: B.gold }} />}
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: selected ? "#f8e9d6" : "rgba(255,255,255,0.85)", fontWeight: selected ? 600 : 400, textAlign: "center" }}>
              {r.patient_first_name} {r.patient_last_name}
            </Typography>
            <Typography sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.35)", textAlign: "center" }}>
              {r.report_type_id} · {formatEST(r.generated_at)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Preview Panel ────────────────────────────────────────────────────────────

function PreviewPanel({ file, onDelete }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [error,      setError]      = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false); // 2026-10-05 — real modal, not window.confirm

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return; }
    setPreviewUrl(null);
    setLoadingUrl(true);
    setError("");
    api.get("/api/reports/preview-url", { params: { blob_name: file.blob_name } })
      .then((r) => setPreviewUrl(r.data.url))
      .catch(() => setError("Could not load preview — GCS may not be configured."))
      .finally(() => setLoadingUrl(false));
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async () => {
    try {
      const r = await api.get("/api/reports/download", { params: { blob_name: file.blob_name } });
      window.open(r.data.url, "_blank");
    } catch { setError("Download failed."); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/api/reports", { params: { blob_name: file.blob_name } });
      onDelete(file.blob_name);
    } catch { setError("Delete failed."); }
    setDeleting(false);
    setConfirmOpen(false);
  };

  if (!file) {
    return (
      <Box sx={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        bgcolor: "#f4f7ff", color: "#cbd5e1", gap: 2,
      }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: "18px",
          background: "linear-gradient(135deg, #e8ecf7, #d8e0f0)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <PictureAsPdf sx={{ fontSize: 36, color: "#94a3b8" }} />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#64748b" }}>
          Select a report to preview
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#94a3b8" }}>
          Click any file tile to open it here
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#f4f7ff", minWidth: 0 }}>
      {/* File info bar */}
      <Box sx={{
        px: 3, py: 2, bgcolor: "#fff",
        borderBottom: "1px solid rgba(0,53,122,0.08)",
        display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
        boxShadow: "0 2px 8px rgba(0,53,122,0.04)",
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Breadcrumbs parts={file.blob_name.split("/").filter(Boolean)} onNavigate={() => {}} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.75, flexWrap: "wrap" }}>
            <Chip label={formatBytes(file.size_bytes)} size="small"
              sx={{ fontSize: 12.5, bgcolor: "rgba(0,53,122,0.06)", color: "#475569",
                border: "1px solid rgba(0,53,122,0.1)", height: 22 }} />
            <Typography sx={{ fontSize: 13.5, color: "#94a3b8" }}>
              {formatDate(file.updated)}
            </Typography>
          </Box>
        </Box>
        {error && <Typography sx={{ fontSize: 13.5, color: "#dc2626" }}>{error}</Typography>}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Download">
            <IconButton size="small" onClick={handleDownload}
              sx={{ bgcolor: "rgba(0,53,122,0.06)", borderRadius: "8px",
                "&:hover": { bgcolor: "rgba(0,53,122,0.12)" } }}>
              <Download sx={{ color: B.navy, fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete permanently">
            <IconButton size="small" onClick={() => setConfirmOpen(true)} disabled={deleting}
              sx={{ bgcolor: "rgba(220,38,38,0.06)", borderRadius: "8px",
                "&:hover": { bgcolor: "rgba(220,38,38,0.12)" } }}>
              {deleting
                ? <CircularProgress size={16} sx={{ color: "#dc2626" }} />
                : <Delete sx={{ color: "#dc2626", fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete this report?"
        message={`Permanently delete "${file.filename}"? This can't be undone.`}
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* PDF Viewer */}
      <Box sx={{ flex: 1, p: 2.5, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {loadingUrl ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={32} sx={{ color: B.navy }} />
          </Box>
        ) : previewUrl ? (
          <Box sx={{
            flex: 1, borderRadius: "12px", overflow: "hidden",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
            border: "1px solid rgba(0,53,122,0.08)",
            display: "flex",
          }}>
            <iframe
              src={previewUrl}
              title="Report Preview"
              style={{ width: "100%", height: "100%", border: "none", minHeight: "600px" }}
            />
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
              Preview not available
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const REPORT_KIND_OPTIONS = [
  { value: "", label: "All kinds" },
  { value: "requisition", label: "Converted Requisitions" },
  { value: "patient", label: "Patient Reports" },
  { value: "clinician", label: "Clinician Reports" },
];

export default function ReportsDashboard() {
  const [currentPrefix, setCurrentPrefix] = useState(""); // "" = lab root
  const [selectedFile,  setSelectedFile]  = useState(null);
  const [reloadKey,     setReloadKey]     = useState(0);

  // ── Multi-select + bulk/folder delete — explicit request: "I need a way
  // to delete the complete folder, not just files" + "multi file select
  // and delete also should be there." checkedItems holds blob_names AND
  // folder prefixes (both end up as GCS prefixes; a single file's "prefix"
  // IS its blob_name) — bulkDelete below sorts out which is which.
  const [selectMode,   setSelectMode]   = useState(false);
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  // 2026-10-08 fix — real bug: handleBulkDelete (below) called a bare
  // `setError(...)` that doesn't exist in this component's scope at all
  // (it was written assuming this function lived inside the Explorer sub-
  // component above, which has its own error state — it actually lives
  // here, in the top-level ReportsDashboard component, which had no
  // generic error state). This was a genuine `no-undef` — the production
  // build has been failing to compile since that fix landed; caught now
  // because this was the first time this app's frontend was actually
  // built in this environment.
  const [bulkDeleteError, setBulkDeleteError] = useState("");

  const toggleCheck = useCallback((key) => {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const exitSelectMode = () => { setSelectMode(false); setCheckedItems(new Set()); };

  // ── Structured filters + free-text filename search ──────────────────────
  // 2026-10-04 — explicit request: real filters (First/Last Name, DOB,
  // Report Kind, Report Type, Session/Accession #), not just a single
  // free-text box. Backed by /api/reports/search's own query params
  // (already built server-side). Accession only applies to Patient/
  // Clinician AI reports — a converted requisition has no accession
  // number — so it's disabled unless Kind is one of those two.
  const [showFilters,   setShowFilters]   = useState(false);
  const [searchInput,   setSearchInput]   = useState("");
  const [filterFirst,   setFilterFirst]   = useState("");
  const [filterLast,    setFilterLast]    = useState("");
  const [filterDob,     setFilterDob]     = useState("");
  const [filterKind,    setFilterKind]    = useState("");
  const [filterAccession, setFilterAccession] = useState("");

  const accessionEnabled = filterKind === "patient" || filterKind === "clinician";

  const activeQuery = useMemo(() => ({
    q: searchInput.trim(),
    first_name: filterFirst.trim(),
    last_name: filterLast.trim(),
    dob: filterDob.trim(),
    report_kind: filterKind,
    accession: accessionEnabled ? filterAccession.trim() : "",
  }), [searchInput, filterFirst, filterLast, filterDob, filterKind, filterAccession, accessionEnabled]);

  const isSearchActive = Object.values(activeQuery).some(Boolean);

  const [debouncedQuery, setDebouncedQuery] = useState(activeQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(activeQuery), 350);
    return () => clearTimeout(t);
  }, [activeQuery]);

  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [searchError,   setSearchError]   = useState("");

  useEffect(() => {
    const active = Object.values(debouncedQuery).some(Boolean);
    if (!active) { setSearchResults([]); return; }
    setSearching(true); setSearchError("");
    const params = Object.fromEntries(Object.entries(debouncedQuery).filter(([, v]) => v));
    api.get("/api/reports/search", { params })
      .then((res) => setSearchResults(res.data.results || []))
      .catch((e) => setSearchError(extractErrorDetail(e, "Search failed.")))
      .finally(() => setSearching(false));
  }, [debouncedQuery]);

  const clearFilters = () => {
    setSearchInput(""); setFilterFirst(""); setFilterLast("");
    setFilterDob(""); setFilterKind(""); setFilterAccession("");
  };

  const handleRefresh = () => {
    setSelectedFile(null);
    setReloadKey((k) => k + 1);
  };

  const handleDelete = (blobName) => {
    if (selectedFile?.blob_name === blobName) setSelectedFile(null);
    setReloadKey((k) => k + 1);
  };

  // 2026-10-05 — explicit request: multi-select + bulk delete, folder
  // delete. checkedItems holds a mix of file blob_names (no trailing "/")
  // and folder prefixes (trailing "/") — split them and hit the matching
  // endpoint for each kind: batch-delete for files, one folder-delete
  // call per checked folder (each folder delete is its own full-prefix
  // sweep server-side, not worth batching into one call).
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    const items = Array.from(checkedItems);
    const folders = items.filter((i) => i.endsWith("/"));
    const files = items.filter((i) => !i.endsWith("/"));
    const failures = [];
    if (files.length) {
      try {
        // 2026-10-15 fix (client-facing audit-log completeness sweep) —
        // batch-delete returns 200 with `ok: false` + a per-item `failed`
        // list on a PARTIAL failure (some blobs deleted, others not), not
        // an HTTP error — a plain try/catch here silently treated that as
        // a full success (no exception was thrown).
        const res = await api.post("/api/reports/batch-delete", { blob_names: files });
        if (!res.data?.ok) throw new Error("partial batch delete");
      } catch (e) {
        failures.push(`${files.length} file(s)`);
      }
    }
    for (const folderPrefix of folders) {
      try {
        // 2026-10-15 fix (client-facing audit-log completeness sweep) —
        // the backend's delete_gcs_folder now isolates each blob's own
        // delete() call (matching batch-delete's existing pattern) and
        // returns 200 with `ok: false` + a `failed` list on a PARTIAL
        // failure, rather than throwing — same contract-mismatch risk as
        // the batch-delete fix above.
        const res = await api.delete("/api/reports/folder", { params: { prefix: folderPrefix } });
        if (!res.data?.ok) throw new Error("partial folder delete");
      } catch (e) {
        failures.push(folderDisplayName(folderPrefix.replace(/\/$/, "").split("/").pop() || folderPrefix));
      }
    }
    if (failures.length) {
      setBulkDeleteError(`Some items could not be deleted: ${failures.join(", ")}. Please retry.`);
    }
    if (selectedFile && checkedItems.has(selectedFile.blob_name)) setSelectedFile(null);
    setBulkDeleting(false);
    setConfirmBulkDelete(false);
    exitSelectMode();
    setReloadKey((k) => k + 1);
  };

  const navigateInto = (folderPrefix) => setCurrentPrefix(folderPrefix);
  const navigateToCrumb = (i) => {
    // i === -1 means "home" (lab root, prefix "")
    if (i === -1) { setCurrentPrefix(""); return; }
    const parts = currentPrefix.replace(/\/$/, "").split("/").filter(Boolean);
    setCurrentPrefix(parts.slice(0, i + 1).join("/") + "/");
  };

  return (
    <PageShell>
      {bulkDeleteError && (
        <Alert severity="error" sx={{ borderRadius: 0 }} onClose={() => setBulkDeleteError("")}>
          {bulkDeleteError}
        </Alert>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 64px - 48px)" }}>

        {/* ── Top bar — title row ──
             2026-10-05 fix — explicit complaint: "search and filters
             merging together, not good." Split into two clearly separate
             rows: title+actions on top, search+filters below as its own
             distinct bar, instead of everything crammed into one flex-wrap
             row where the boundary between title and controls was unclear. */}
        <Box sx={{
          px: 3, py: 2, bgcolor: "#fff", flexShrink: 0,
          borderBottom: "1px solid rgba(0,53,122,0.08)",
          boxShadow: "0 2px 8px rgba(0,53,122,0.04)",
          display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", rowGap: 1,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: "10px",
              background: `linear-gradient(135deg, ${B.navy}, ${B.navyMid})`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <CloudDone sx={{ color: B.gold, fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: B.navy, fontSize: 18, letterSpacing: "-0.02em", fontFamily: "'Playfair Display', serif" }}>
                Report Storage
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "#94a3b8", fontFamily: "'Inter', sans-serif" }}>
                Browse, download and manage all diagnostic reports
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* 2026-10-05 — explicit request: multi-select + bulk/folder delete */}
          <Button
            size="small" onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
            startIcon={<CheckBoxOutlineBlank fontSize="small" />}
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: "10px",
              color: selectMode ? "#fff" : B.navy,
              bgcolor: selectMode ? B.navy : "rgba(0,53,122,0.06)",
              "&:hover": { bgcolor: selectMode ? B.navyMid : "rgba(0,53,122,0.1)" },
            }}>
            {selectMode ? "Cancel Select" : "Select"}
          </Button>

          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh}
              sx={{ bgcolor: "rgba(0,53,122,0.06)", borderRadius: "10px",
                "&:hover": { bgcolor: "rgba(0,53,122,0.1)" } }}>
              <Refresh sx={{ color: B.navy, fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Search + Filters row — its own distinct bar, separate from the title ── */}
        <Box sx={{
          px: 3, py: 1.5, bgcolor: "#fafbfe", flexShrink: 0,
          borderBottom: "1px solid rgba(0,53,122,0.06)",
          display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
        }}>
          <TextField
            size="small"
            placeholder="Search filenames…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ width: 240, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 17, color: "#94a3b8" }} /></InputAdornment>,
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput("")}><Close sx={{ fontSize: 15 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Tooltip title="Filters">
            <Button
              size="small" onClick={() => setShowFilters((v) => !v)}
              startIcon={<FilterList fontSize="small" />}
              sx={{
                textTransform: "none", fontWeight: 600, borderRadius: "10px",
                color: showFilters ? "#fff" : B.navy,
                bgcolor: showFilters ? B.navy : "rgba(0,53,122,0.06)",
                "&:hover": { bgcolor: showFilters ? B.navyMid : "rgba(0,53,122,0.1)" },
              }}>
              Filters
            </Button>
          </Tooltip>
        </Box>

        {/* ── Filter bar (collapsible) ── */}
        <Collapse in={showFilters}>
          <Box sx={{
            px: 3, py: 1.75, bgcolor: "#f8fafc", borderBottom: "1px solid rgba(0,53,122,0.08)",
            display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center",
          }}>
            <Typography sx={{ flexBasis: "100%", fontSize: 12, color: "#888" }}>
              First/Last Name search is an exact match for newly generated reports (not a
              partial/substring match) — enter the name as it appears on the report.
            </Typography>
            <TextField size="small" label="First Name" value={filterFirst}
              onChange={(e) => setFilterFirst(e.target.value)}
              sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff" } }} />
            <TextField size="small" label="Last Name" value={filterLast}
              onChange={(e) => setFilterLast(e.target.value)}
              sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff" } }} />
            <TextField size="small" label="Date of Birth" type="date" value={filterDob}
              onChange={(e) => setFilterDob(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff" } }} />
            <Select size="small" displayEmpty value={filterKind}
              onChange={(e) => { setFilterKind(e.target.value); if (e.target.value !== "patient" && e.target.value !== "clinician") setFilterAccession(""); }}
              sx={{ width: 190, borderRadius: "8px", bgcolor: "#fff" }}>
              {REPORT_KIND_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
            <Tooltip title={accessionEnabled ? "" : "Only applies to Patient/Clinician AI reports — select that Kind first"}>
              <span>
                <TextField size="small" label="Accession / Session #" value={filterAccession}
                  disabled={!accessionEnabled}
                  onChange={(e) => setFilterAccession(e.target.value)}
                  sx={{ width: 190, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff" } }} />
              </span>
            </Tooltip>
            {isSearchActive && (
              <Button size="small" onClick={clearFilters}
                sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}>
                Clear all
              </Button>
            )}
          </Box>
        </Collapse>

        {/* ── Body ── */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>

          {/* Left — explorer / search results */}
          <Box sx={{
            width: "46%", minWidth: 420, flexShrink: 0,
            // 2026-10-04 fix — real color mismatch, reported directly: this
            // rgba(0,37,96,...) is the exact same stale navy (#002560)
            // already found and fixed as hex strings elsewhere this
            // session — missed here since it was expressed as an rgba()
            // tuple, not a hex literal. #001f4d = rgb(0,31,77).
            bgcolor: "rgba(0,31,77,0.97)",
            borderRight: "1px solid rgba(192,168,140,0.15)",
            display: "flex", flexDirection: "column",
            overflow: "hidden",
          }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              {selectMode ? (
                // 2026-10-05 — bulk-action bar: shows the moment Select
                // mode is on, count updates live, Delete only enabled once
                // something's actually checked.
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#fff", flex: 1 }}>
                    {checkedItems.size} selected
                  </Typography>
                  <Button size="small" disabled={checkedItems.size === 0}
                    onClick={() => setConfirmBulkDelete(true)}
                    startIcon={<DeleteSweep fontSize="small" />}
                    sx={{
                      textTransform: "none", fontWeight: 700, borderRadius: "8px", fontSize: "0.72rem",
                      color: "#fff", bgcolor: "rgba(220,38,38,0.85)",
                      "&:hover": { bgcolor: "#dc2626" },
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.06)" },
                    }}>
                    Delete
                  </Button>
                </Box>
              ) : (
                <Typography sx={{
                  fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em",
                  color: "rgba(255,255,255,0.3)", textTransform: "uppercase",
                }}>
                  {isSearchActive ? "Search Results" : "Cloud Storage"}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, overflow: "hidden" }}>
              {isSearchActive ? (
                <Box sx={{ height: "100%", overflowY: "auto" }}>
                  <SearchResultsGrid
                    results={searchResults} loading={searching} error={searchError}
                    query={searchInput} onFileSelect={setSelectedFile}
                    selectedBlob={selectedFile?.blob_name}
                  />
                </Box>
              ) : (
                <Explorer
                  currentPrefix={currentPrefix}
                  onNavigateInto={navigateInto}
                  onNavigateToCrumb={navigateToCrumb}
                  onFileSelect={setSelectedFile}
                  selectedBlob={selectedFile?.blob_name}
                  reloadKey={reloadKey}
                  selectMode={selectMode}
                  checkedItems={checkedItems}
                  onToggleCheck={toggleCheck}
                />
              )}
            </Box>

            {/* 2026-10-04 fix — explicit instruction: don't expose the
                underlying cloud vendor to end users — that's an internal
                infra detail, not something staff need to see. */}
          </Box>

          {/* Right panel — preview */}
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedFile?.blob_name || "empty"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}
            >
              <PreviewPanel file={selectedFile} onDelete={handleDelete} />
            </motion.div>
          </AnimatePresence>

        </Box>
      </Box>

      <ConfirmDialog
        open={confirmBulkDelete}
        title="Delete selected items?"
        message={`Permanently delete ${checkedItems.size} selected item${checkedItems.size === 1 ? "" : "s"}? Any selected folder deletes everything inside it too. This can't be undone.`}
        loading={bulkDeleting}
        onConfirm={handleBulkDelete}
        onCancel={() => setConfirmBulkDelete(false)}
      />
    </PageShell>
  );
}
